/* eslint-disable react/jsx-no-constructed-context-values */

'use client'

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react'
import { Box, Snackbar, Typography } from '@mui/material'
import MuiAlert, { AlertProps } from '@mui/material/Alert'
import { useUserState } from '../login-state-management'

interface SnackbarContextType {
  showSnackbar: (
    message: string,
    severity?: AlertProps['severity'],
    duration?: number
  ) => void
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(
  undefined
)

export const useSnackbar = () => {
  const context = useContext(SnackbarContext)
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider')
  }
  return context
}

interface SnackbarProviderProps {
  children: ReactNode
}

export function SnackbarProvider({ children }: SnackbarProviderProps) {
  const { userDetails } = useUserState()
  const [snackbarState, setSnackbarState] = useState<{
    message: string
    open: boolean
    severity?: AlertProps['severity']
    duration?: number
  }>({
    message: 'Hello from Snackbar',
    open: false,
    severity: 'info',
    duration: 4000,
  })

  const showSnackbar = (
    message: string,
    severity: AlertProps['severity'] = 'info',
    duration: number = 3000
  ) => {
    setSnackbarState({ message, open: true, severity, duration })
  }

  const normalizedMessage = snackbarState.message.toLowerCase()
  const isCitySelectionWarning =
    normalizedMessage.includes('select your city') ||
    normalizedMessage.includes('select a city')

  const handleClose = () => {
    if (isCitySelectionWarning && !userDetails?.cityId) return
    setSnackbarState((prevState) => ({ ...prevState, open: false }))
  }

  useEffect(() => {
    if (isCitySelectionWarning && userDetails?.cityId) {
      setSnackbarState((prevState) => ({ ...prevState, open: false }))
    }
  }, [isCitySelectionWarning, userDetails?.cityId])

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}
      <Snackbar
        className={
          isCitySelectionWarning ? 'city-selection-warning' : undefined
        }
        open={
          snackbarState.open &&
          !(isCitySelectionWarning && Boolean(userDetails?.cityId))
        }
        autoHideDuration={
          isCitySelectionWarning ? null : snackbarState.duration
        }
        onClose={handleClose}
        anchorOrigin={
          isCitySelectionWarning
            ? { vertical: 'top', horizontal: 'left' }
            : { vertical: 'bottom', horizontal: 'left' }
        }
        sx={
          isCitySelectionWarning
            ? {
                top: { xs: '76px', lg: '72px' },
                left: {
                  xs: '50%',
                  lg: 'clamp(265px, 20vw, 330px)',
                },
                right: 'auto',
                transform: { xs: 'translateX(-50%)', lg: 'none' },
              }
            : undefined
        }
      >
        {isCitySelectionWarning ? (
          <Box
            role="alert"
            sx={{
              position: 'relative',
              padding: '9px 22px',
              border: '1px solid #08798b',
              borderRadius: '999px',
              backgroundColor: '#fff',
              color: '#08798b',
              boxShadow: '0 6px 18px rgba(15, 23, 42, .16)',
              '&::before': {
                content: '""',
                position: 'absolute',
                left: '50%',
                top: '-9px',
                width: '16px',
                height: '16px',
                backgroundColor: '#fff',
                borderLeft: '1px solid #08798b',
                borderTop: '1px solid #08798b',
                transform: 'translateX(-50%) rotate(45deg)',
              },
            }}
          >
            <Typography
              sx={{
                position: 'relative',
                fontSize: '13px',
                fontWeight: 800,
                letterSpacing: '.14em',
                lineHeight: 1.2,
              }}
            >
              SELECT CITY
            </Typography>
          </Box>
        ) : (
          <MuiAlert
            onClose={handleClose}
            severity={snackbarState.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbarState.message}
          </MuiAlert>
        )}
      </Snackbar>
    </SnackbarContext.Provider>
  )
}
